import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaUser, FaSearch, FaEdit, FaCheckCircle, FaClock, FaEnvelope, FaTimes } from 'react-icons/fa';
import adminService, { AgencyRequest, FilterCounts, PaginationData } from '../services/adminService';
import LoadingSpinner from './LoadingSpinner';
import { toast } from 'react-toastify';

const AgencyRequests: React.FC = () => {
    const [requests, setRequests] = useState<AgencyRequest[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [hasUserFilter, setHasUserFilter] = useState('');
    const [isProcessedFilter, setIsProcessedFilter] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage] = useState(10);
    const [pagination, setPagination] = useState<PaginationData | null>(null);
    const [filterCounts, setFilterCounts] = useState<FilterCounts | null>(null);
    const [selectedRequest, setSelectedRequest] = useState<AgencyRequest | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [notes, setNotes] = useState('');

    useEffect(() => {
        fetchRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, perPage, searchTerm, hasUserFilter, isProcessedFilter, sortBy, sortOrder]);

    const fetchRequests = async () => {
        try {
            setIsLoading(true);
            const params: any = {
                page: currentPage,
                limit: perPage,
                sortBy,
                sortOrder,
                ...(searchTerm && { search: searchTerm }),
                ...(hasUserFilter && { hasUser: hasUserFilter }),
                ...(isProcessedFilter && { isProcessed: isProcessedFilter }),
            };

            const response = await adminService.getAgencyRequests(params);
            const data = response.data || response;

            setRequests(data.agencyRequests || []);
            setPagination(data.pagination || null);
            setFilterCounts(data.filterCounts || null);
        } catch (error: any) {
            console.error('Error fetching agency requests:', error);
            toast.error(error.response?.data?.message || 'Failed to load agency requests');
            setRequests([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleStatusUpdate = async (requestId: string, isProcessed: boolean) => {
        try {
            setIsUpdating(true);
            await adminService.updateAgencyRequestStatus(requestId, { isProcessed, notes });
            toast.success('Status updated successfully');
            fetchRequests();
            setSelectedRequest(null);
            setNotes('');
        } catch (error: any) {
            console.error('Error updating status:', error);
            toast.error(error.response?.data?.message || 'Failed to update status');
        } finally {
            setIsUpdating(false);
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <FaClock className="text-orange-500" />
                    Agency Requests
                </h1>
                <p className="text-gray-600">View and manage agency form submissions from the landing page</p>
            </div>

            {/* Filter Stats */}
            {filterCounts && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="text-sm text-gray-600">Total Requests</div>
                        <div className="text-2xl font-bold text-blue-600">{filterCounts.total || 0}</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="text-sm text-gray-600">With User</div>
                        <div className="text-2xl font-bold text-green-600">{filterCounts.withUser || 0}</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="text-sm text-gray-600">Anonymous</div>
                        <div className="text-2xl font-bold text-orange-600">{filterCounts.withoutUser || 0}</div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="text-sm text-gray-600">Unprocessed</div>
                        <div className="text-2xl font-bold text-red-600">{filterCounts.unprocessed || 0}</div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="relative">
                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by name, email, or agency..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && setCurrentPage(1)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <select
                        value={hasUserFilter}
                        onChange={(e) => {
                            setHasUserFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">All Users</option>
                        <option value="true">With User</option>
                        <option value="false">Anonymous</option>
                    </select>
                    <select
                        value={isProcessedFilter}
                        onChange={(e) => {
                            setIsProcessedFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="">All Status</option>
                        <option value="false">Unprocessed</option>
                        <option value="true">Processed</option>
                    </select>
                    <select
                        value={`${sortBy}-${sortOrder}`}
                        onChange={(e) => {
                            const [field, order] = e.target.value.split('-');
                            setSortBy(field);
                            setSortOrder(order);
                            setCurrentPage(1);
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="createdAt-desc">Newest First</option>
                        <option value="createdAt-asc">Oldest First</option>
                        <option value="name-asc">Name A-Z</option>
                        <option value="name-desc">Name Z-A</option>
                        <option value="agencyName-asc">Agency A-Z</option>
                        <option value="agencyName-desc">Agency Z-A</option>
                    </select>
                </div>
            </div>

            {/* Requests List */}
            {isLoading ? (
                <LoadingSpinner />
            ) : requests.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                    <FaClock className="mx-auto text-4xl text-gray-400 mb-4" />
                    <p className="text-gray-600 text-lg">No agency requests found</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {requests.map((request) => (
                        <motion.div
                            key={request._id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow"
                        >
                            <div className="flex justify-between items-start mb-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-3">
                                        {request.isProcessed ? (
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 flex items-center gap-1">
                                                <FaCheckCircle className="text-xs" /> Processed
                                            </span>
                                        ) : (
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 flex items-center gap-1">
                                                <FaClock className="text-xs" /> Pending
                                            </span>
                                        )}
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                                        <div>
                                            <div className="text-sm text-gray-600 mb-1">Name</div>
                                            <div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                                {request.userId ? (
                                                    <FaUser className="text-blue-600" />
                                                ) : (
                                                    <FaUser className="text-gray-400 opacity-50" />
                                                )}
                                                {request.name}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-sm text-gray-600 mb-1">Agency Name</div>
                                            <div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                                <FaClock className="text-orange-500" />
                                                {request.agencyName}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                                        <div className="flex items-center gap-2">
                                            <FaEnvelope className="text-blue-600" />
                                            <span>{request.email}</span>
                                        </div>
                                        {request.userId && (
                                            <div className="flex items-center gap-2">
                                                <FaUser className="text-blue-600" />
                                                <span>
                                                    User: {request.userId?.name || 'N/A'} ({request.userId?.email || 'N/A'})
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-1">
                                            <FaClock className="text-xs" />
                                            <span>{formatDate(request.createdAt)}</span>
                                        </div>
                                    </div>
                                    {request.notes && (
                                        <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                            <p className="text-sm text-gray-700">
                                                <strong>Notes:</strong> {request.notes}
                                            </p>
                                        </div>
                                    )}
                                </div>
                                <div className="flex gap-2 ml-4">
                                    <button
                                        onClick={() => {
                                            setSelectedRequest(request);
                                            setNotes(request.notes || '');
                                        }}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                                    >
                                        <FaEdit /> {request.isProcessed ? 'Update' : 'Mark Processed'}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-6">
                    <button
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        disabled={!pagination.hasPrevPage}
                        className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                        Previous
                    </button>
                    <span className="text-gray-600">
                        Page {pagination.currentPage} of {pagination.totalPages}
                    </span>
                    <button
                        onClick={() => setCurrentPage((prev) => prev + 1)}
                        disabled={!pagination.hasNextPage}
                        className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                        Next
                    </button>
                </div>
            )}

            {/* Update Status Modal */}
            {selectedRequest && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
                    >
                        <h3 className="text-xl font-bold mb-4">Update Request Status</h3>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Mark as Processed
                            </label>
                            <div className="flex gap-4">
                                <button
                                    onClick={() => handleStatusUpdate(selectedRequest._id, true)}
                                    disabled={isUpdating}
                                    className={`px-4 py-2 rounded-lg font-medium ${selectedRequest.isProcessed
                                        ? 'bg-green-500 text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-green-500 hover:text-white'
                                        } transition-colors`}
                                >
                                    <FaCheckCircle className="inline mr-2" />
                                    Processed
                                </button>
                                <button
                                    onClick={() => handleStatusUpdate(selectedRequest._id, false)}
                                    disabled={isUpdating}
                                    className={`px-4 py-2 rounded-lg font-medium ${!selectedRequest.isProcessed
                                        ? 'bg-yellow-500 text-white'
                                        : 'bg-gray-200 text-gray-700 hover:bg-yellow-500 hover:text-white'
                                        } transition-colors`}
                                >
                                    <FaTimes className="inline mr-2" />
                                    Pending
                                </button>
                            </div>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                rows={3}
                                placeholder="Add notes about this request..."
                            />
                        </div>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setSelectedRequest(null);
                                    setNotes('');
                                }}
                                disabled={isUpdating}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => handleStatusUpdate(selectedRequest._id, selectedRequest.isProcessed)}
                                disabled={isUpdating}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isUpdating ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default AgencyRequests;
